import React, { useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ScrollArea } from '../../ui/scroll-area';
import { 
  ArrowLeft, ShoppingCart, RefreshCw, Receipt, Search,
  X, Plus, Minus, Wine, UtensilsCrossed, ShoppingBag, Check
} from 'lucide-react';

const SECTION_TABS = {
  'barra': { icon: Wine, color: '#9333EA', label: 'Barra' },
  'cocina': { icon: UtensilsCrossed, color: '#EA580C', label: 'Cocina' },
  'tienda': { icon: ShoppingBag, color: '#0D9488', label: 'Tienda' }
};

const ProductSelectorView = ({
  mesa,
  categories,
  products,
  cart,
  searchQuery,
  sendingOrder,
  onBack,
  onSearchChange,
  onAddToCart,
  onUpdateQuantity,
  onClearCart,
  onSendOrder,
  getProductName,
  getCartTotal,
  getCartCount
}) => {
  const [selectedSection, setSelectedSection] = useState('barra');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectionModal, setSelectionModal] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedSupplements, setSelectedSupplements] = useState([]);

  // Group categories by product seccion
  const groupedCategories = useMemo(() => {
    const groups = { barra: [], cocina: [], tienda: [] };
    if (!categories || !Array.isArray(categories)) return groups;
    
    categories.forEach(catName => {
      const catProducts = products[catName] || [];
      if (catProducts.length === 0) return;
      
      // Use the seccion of the first product in this category
      const seccion = catProducts[0]?.seccion || 'cocina';
      if (groups[seccion]) {
        groups[seccion].push(catName);
      } else {
        groups['cocina'].push(catName);
      }
    });
    return groups;
  }, [categories, products]);

  React.useEffect(() => {
    const sectionCats = groupedCategories[selectedSection];
    if (sectionCats && sectionCats.length > 0 && !sectionCats.includes(selectedCategory)) {
      setSelectedCategory(sectionCats[0]);
    }
  }, [selectedSection, groupedCategories, selectedCategory]);

  const filteredProducts = useMemo(() => {
    if (!selectedCategory || !products[selectedCategory]) return [];
    const prods = products[selectedCategory];
    if (!searchQuery) return prods;
    return prods.filter(p => {
      const name = getProductName(p).toLowerCase();
      return name.includes(searchQuery.toLowerCase());
    });
  }, [selectedCategory, products, searchQuery, getProductName]);

  const productNeedsSelection = (product) => {
    return product.requires_selection || 
           product.requires_bread_selection || 
           (product.options && product.options.length > 0) ||
           (product.supplements && product.supplements.length > 0);
  };

  const handleProductClick = (product) => {
    if (productNeedsSelection(product)) {
      setSelectionModal(product);
      setSelectedOptions({});
      setSelectedSupplements([]);
    } else {
      onAddToCart(product);
    }
  };

  const getOptionGroups = (product) => {
    const groups = {};
    (product.options || []).forEach(opt => {
      const group = opt.group || 'opciones';
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    });
    return groups;
  };

  const getGroupLabel = (group) => {
    const labels = {
      'tipo_pan': 'Tipo de Pan',
      'ensalada': 'Ensalada',
      'principal': 'Plato Principal',
      'plato_principal': 'Plato Principal',
      'postre': 'Postre',
      'tamano': 'Tamaño',
      'temperatura': 'Temperatura',
      'opciones': 'Opciones'
    };
    return labels[group] || group.charAt(0).toUpperCase() + group.slice(1).replace(/_/g, ' ');
  };

  const getRequiredCount = (product, group) => {
    if (product.requires_bread_selection && group === 'tipo_pan') return 1;
    if (product.selection_requirements) {
      return product.selection_requirements[group] || 0;
    }
    // Default: 1 selection per group for products with options
    if (product.options?.some(o => o.group === group)) return 1;
    return 0;
  };

  const handleSelectOption = (group, option) => {
    const product = selectionModal;
    const required = getRequiredCount(product, group);
    
    setSelectedOptions(prev => {
      const current = prev[group] || [];
      const optName = option.name?.es || option.name || '';
      const isSelected = current.some(o => (o.name?.es || o.name) === optName);
      
      if (isSelected) {
        return { ...prev, [group]: current.filter(o => (o.name?.es || o.name) !== optName) };
      }
      
      if (required === 1) {
        return { ...prev, [group]: [option] };
      }
      
      if (current.length >= required && required > 0) {
        return { ...prev, [group]: [...current.slice(1), option] };
      }
      
      return { ...prev, [group]: [...current, option] };
    });
  };

  const handleToggleSupplement = (supplement) => {
    const supName = supplement.name?.es || supplement.name || '';
    setSelectedSupplements(prev => {
      const isSelected = prev.some(s => (s.name?.es || s.name) === supName);
      if (isSelected) return prev.filter(s => (s.name?.es || s.name) !== supName);
      return [...prev, supplement];
    });
  };

  const isSelectionComplete = () => {
    if (!selectionModal) return false;
    const product = selectionModal;
    const optionGroups = getOptionGroups(product);
    
    for (const group of Object.keys(optionGroups)) {
      const required = getRequiredCount(product, group);
      const selected = (selectedOptions[group] || []).length;
      if (required > 0 && selected < required) return false;
    }
    return true;
  };

  const handleConfirmSelection = () => {
    if (!selectionModal) return;
    
    const product = selectionModal;
    const allSelected = [];
    let extraCost = 0;
    
    Object.values(selectedOptions).flat().forEach(opt => {
      const name = opt.name?.es || opt.name || '';
      allSelected.push(name);
      extraCost += opt.price_modifier || 0;
    });
    
    selectedSupplements.forEach(sup => {
      const name = sup.name?.es || sup.name || '';
      allSelected.push(`+${name}`);
      extraCost += sup.price_modifier || 0;
    });
    
    const productWithOptions = {
      ...product,
      name: getProductName(product),
      selectedOptions: allSelected,
      optionsText: allSelected.join(', '),
      price: product.price + extraCost,
      originalPrice: product.price,
      extraCost
    };
    
    onAddToCart(productWithOptions);
    setSelectionModal(null);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50" data-testid="add-products-view">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h2 className="font-bold text-lg">Mesa {mesa.numero}</h2>
            <p className="text-xs text-gray-500">{mesa.status === 'libre' ? 'Nueva orden' : 'Añadir productos'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getCartCount() > 0 && (
            <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
              <ShoppingCart size={18} className="text-green-600" />
              <span className="font-bold text-green-700">{getCartCount()}</span>
              <span className="text-green-600 font-semibold">{getCartTotal().toFixed(2)}€</span>
            </div>
          )}
          <Button 
            onClick={onSendOrder} 
            disabled={cart.length === 0 || sendingOrder}
            className="bg-green-600 hover:bg-green-700"
          >
            {sendingOrder ? <RefreshCw className="animate-spin mr-2" size={16} /> : null}
            Enviar Pedido
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Categories sidebar */}
        <div className="w-48 bg-white border-r flex flex-col">
          <div className="flex border-b">
            {Object.entries(SECTION_TABS).map(([key, config]) => {
              const Icon = config.icon;
              const count = groupedCategories[key]?.length || 0;
              if (count === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedSection(key)}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${
                    selectedSection === key 
                      ? 'border-b-2 text-gray-900' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={{ borderColor: selectedSection === key ? config.color : 'transparent' }}
                  data-testid={`section-tab-${key}`}
                >
                  <Icon size={16} className="mx-auto mb-1" style={{ color: selectedSection === key ? config.color : undefined }} />
                  {config.label}
                </button>
              );
            })}
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {groupedCategories[selectedSection]?.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-gray-100 font-medium text-gray-900' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Products grid */}
        <div className="flex-1 flex flex-col">
          <div className="p-3 border-b bg-white">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar producto..."
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filteredProducts.map(product => {
                const cartItems = cart.filter(c => c.id === product.id);
                const totalInCart = cartItems.reduce((sum, c) => sum + c.quantity, 0);
                const needsSelection = productNeedsSelection(product);
                return (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className={`relative p-3 bg-white border rounded-xl text-left hover:shadow-md transition-all ${
                      totalInCart > 0 ? 'ring-2 ring-green-500 bg-green-50' : ''
                    }`}
                    data-testid={`quick-product-${product.id}`}
                  >
                    {totalInCart > 0 && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {totalInCart}
                      </div>
                    )}
                    <p className="font-medium text-sm text-gray-900 line-clamp-2">{getProductName(product)}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-green-600 font-bold">{product.price?.toFixed(2)}€</p>
                      {needsSelection && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">Opciones</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Cart sidebar */}
        {cart.length > 0 && (
          <div className="w-72 bg-white border-l flex flex-col">
            <div className="p-3 border-b font-medium flex items-center justify-between">
              <span>Pedido</span>
              <Button variant="ghost" size="sm" onClick={onClearCart} className="text-red-500 h-8">
                <X size={16} />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {cart.map((item, idx) => (
                  <div key={item.cartId || `${item.id}-${idx}`} className="bg-gray-50 p-2 rounded-lg">
                    <p className="text-sm font-medium line-clamp-1">{item.name}</p>
                    {item.optionsText && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.optionsText}</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-1">
                        <button onClick={() => onUpdateQuantity(item.cartId || item.id, -1)} className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                          <Minus size={12} />
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                        <button onClick={() => onUpdateQuantity(item.cartId || item.id, 1)} className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="font-semibold text-sm">{(item.price * item.quantity).toFixed(2)}€</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="p-3 border-t bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Total</span>
                <span className="text-xl font-bold text-green-600">{getCartTotal().toFixed(2)}€</span>
              </div>
              <Button onClick={onSendOrder} disabled={sendingOrder} className="w-full bg-green-600 hover:bg-green-700">
                {sendingOrder ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Receipt size={16} className="mr-2" />}
                Enviar a Mesa {mesa.numero}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Selection Modal */}
      {selectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="selection-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-gray-900">{getProductName(selectionModal)}</h3>
                <p className="text-green-600 font-semibold">{selectionModal.price?.toFixed(2)}€</p>
              </div>
              <button onClick={() => setSelectionModal(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <ScrollArea className="flex-1 overflow-y-auto">
              <div className="p-5 space-y-5">
                {/* Option Groups */}
                {Object.entries(getOptionGroups(selectionModal)).map(([group, options]) => {
                  const required = getRequiredCount(selectionModal, group);
                  const selected = selectedOptions[group] || [];
                  const isComplete = required > 0 && selected.length >= required;
                  
                  return (
                    <div key={group}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-800">{getGroupLabel(group)}</h4>
                        {required > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isComplete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {isComplete ? <Check size={12} className="inline mr-0.5" /> : null}
                            {selected.length}/{required} {required === 1 ? 'seleccionado' : 'seleccionados'}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        {options.map((option, idx) => {
                          const optName = option.name?.es || option.name || '';
                          const isSelected = selected.some(o => (o.name?.es || o.name) === optName);
                          return (
                            <button
                              key={idx}
                              onClick={() => handleSelectOption(group, option)}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all flex items-center justify-between ${
                                isSelected 
                                  ? 'border-green-500 bg-green-50 text-green-800' 
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <span className="text-sm font-medium">{optName}</span>
                              <div className="flex items-center gap-2">
                                {option.price_modifier > 0 && (
                                  <span className="text-xs text-gray-500">+{option.price_modifier.toFixed(2)}€</span>
                                )}
                                {isSelected && <Check size={16} className="text-green-600" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Supplements */}
                {selectionModal.supplements && selectionModal.supplements.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Extras (opcional)</h4>
                    <div className="space-y-1.5">
                      {selectionModal.supplements.map((sup, idx) => {
                        const supName = sup.name?.es || sup.name || '';
                        const isSelected = selectedSupplements.some(s => (s.name?.es || s.name) === supName);
                        return (
                          <button
                            key={idx}
                            onClick={() => handleToggleSupplement(sup)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all flex items-center justify-between ${
                              isSelected 
                                ? 'border-blue-500 bg-blue-50 text-blue-800' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span className="text-sm font-medium">{supName}</span>
                            <div className="flex items-center gap-2">
                              {sup.price_modifier > 0 && (
                                <span className="text-xs text-gray-500">+{sup.price_modifier.toFixed(2)}€</span>
                              )}
                              {isSelected && <Check size={16} className="text-blue-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Modal Footer */}
            <div className="px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
              <Button 
                onClick={handleConfirmSelection}
                disabled={!isSelectionComplete()}
                className="w-full bg-green-600 hover:bg-green-700 h-12 text-base disabled:opacity-50"
                data-testid="confirm-selection-btn"
              >
                {isSelectionComplete() ? (
                  <>
                    <Check size={18} className="mr-2" />
                    Añadir al pedido
                  </>
                ) : (
                  'Completa las selecciones obligatorias'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelectorView;
